/** CSS px at 96/inch. A4 210×297mm, A3 297×420mm. */
export const PAGE_FORMATS_PX = {
  a4: { width: 794, height: 1123 },
  a3: { width: 1123, height: 1587 },
} as const;

/** minimales ≈5mm, normales ≈15mm (the previous fixed margin), larges ≈25mm. */
export const MARGIN_PX = { minimales: 19, normales: 57, larges: 95 } as const;
/** Same three values as CSS lengths for Playwright's page.pdf margin option. */
export const MARGIN_MM = { minimales: '5mm', normales: '15mm', larges: '25mm' } as const;

/** Cell inner padding per density (spec R2). */
export const DENSITY_PADDING_PX = { compact: 4, normal: 8, aere: 14 } as const;

export const CSS_PX_PER_PT = 96 / 72;
/** Line-height factor used by every row-height estimate and by the stylesheet. */
export const LINE_HEIGHT_FACTOR = 1.3;

export const DEFAULT_COLUMN_WIDTH_PX = 220;
export const DEFAULT_ROW_HEIGHT_PX = 120;
export const ROW_LABEL_WIDTH_PX = 160;

/**
 * Conservative, uniform-per-page vertical reservations for overhead that shares
 * physical page space with the tiled table grid but isn't accounted for by
 * `computeTilePages`' column/row sizing math. See `compute-tile-pages.ts`'s
 * `reservedHeaderHeightPx` for how these are combined and applied.
 */
/** Covers a two-line column-header cell with 8px padding top/bottom, rendered on any tile where `repeatHeaders` is true or it's the first row-page. */
export const HEADER_ROW_RESERVED_PX = 60;
/** Covers one paragraph of the optional subject/description block — applied only when that block is included. */
export const SUBJECT_DESCRIPTION_RESERVED_PX = 60;
/**
 * Air between the last grid row and the footer — the part of
 * `FOOTER_RESERVED_PX` the footer box itself does not occupy.
 */
export const FOOTER_GAP_PX = 10;
/**
 * Bottom-of-page reservation for the document footer (spec R4): its 14px mark
 * (the PMS signature image height, which the stylesheet pins the footer box
 * to) plus `FOOTER_GAP_PX`. Charged to every page's usable height whenever
 * `documentFooterEnabled` — a chantier 1 carry-over, where the footer was
 * drawn over a full page's last row instead.
 */
export const FOOTER_RESERVED_PX = 24;

export interface PageGeometry {
  pageWidthPx: number; // oriented
  pageHeightPx: number; // oriented
  marginPx: number;
}

export function pageGeometry(input: {
  format: keyof typeof PAGE_FORMATS_PX;
  orientation: 'portrait' | 'landscape';
  margins: keyof typeof MARGIN_PX;
}): PageGeometry {
  const { width, height } = PAGE_FORMATS_PX[input.format];
  return {
    pageWidthPx: input.orientation === 'landscape' ? height : width,
    pageHeightPx: input.orientation === 'landscape' ? width : height,
    marginPx: MARGIN_PX[input.margins],
  };
}
